import { useState } from "react";
import { ArrowUpRight, ArrowDownRight, BarChart3, Sparkles, ChevronDown, ChevronUp, LayoutGrid } from "lucide-react";

export const FILL = {
  emerald: "linear-gradient(160deg,#1c8a4b,#0b5a2e 60%,#063a1d)", blue: "linear-gradient(160deg,#2a63c4,#123b8a 60%,#0a2360)",
  gold: "linear-gradient(160deg,#c98a2c,#8a4f10 60%,#5a2f06)", purple: "linear-gradient(160deg,#6d3bb8,#43208a 60%,#2a0f5c)",
  teal: "linear-gradient(160deg,#16909b,#0c5e69 60%,#063c44)", ruby: "linear-gradient(160deg,#c0262f,#7a0f1d 60%,#4a080e)",
  green: "linear-gradient(160deg,#3a9a2a,#1f6b14 60%,#123f0b)", stone: "linear-gradient(160deg,#8a8378,#5c564d 60%,#3b3730)",
};

export function OwnerKpi({ i, fill, Icon, label, value, sub, trend, trendLabel, status, onClick, testid }) {
  return (
    <button type="button" onClick={onClick} className="bi-tile !items-center !justify-center gap-1 text-center !min-h-[150px] sm:!min-h-[168px] !px-2 sm:!px-4"
      style={{ "--bi-fill": FILL[fill], animationDelay: `${i * 30}ms` }} data-testid={testid}>
      {Icon && <Icon className="h-9 w-9 text-[#F4D58D]" />}
      <span className="bi-tile-label">{label}</span>
      <span className="bi-tile-value !text-[1.15rem] sm:!text-[1.55rem] break-all" data-testid={`${testid}-value`}>{value}</span>
      {status && <span className="rounded-full bg-black/25 px-2 py-0.5 text-xs font-bold uppercase tracking-wide" data-testid={`${testid}-status`}>{status}</span>}
      {trend != null && (
        <span className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-white/90 px-2 py-0.5 text-sm font-bold text-[#3b1a12]" data-testid={`${testid}-trend`}>
          {trend >= 0 ? <ArrowUpRight className="h-4 w-4 text-emerald-700" /> : <ArrowDownRight className="h-4 w-4 text-red-700" />}
          {trend >= 0 ? "+" : "−"}{Math.abs(trend).toFixed(1)}%{trendLabel ? ` ${trendLabel}` : ""}
        </span>)}
      {sub && <span className="text-xs opacity-90 leading-tight">{sub}</span>}
    </button>
  );
}

export function InsightsBanner({ periodLabel, summary, loading }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-[22px] border-[3px] border-[#d9ae52] p-4 sm:p-5 text-[#fff3c4]" data-testid="or-insights-banner"
      style={{ background: "radial-gradient(120% 80% at 0% 0%, rgba(244,213,141,0.22), transparent 55%), linear-gradient(160deg,#8f1524,#750817 55%,#4D0712)" }}>
      <div className="flex flex-col lg:flex-row gap-4 lg:items-center">
        <div className="flex items-center gap-3 lg:w-[38%] lg:border-r-2 lg:border-[#d9ae52]/60 lg:pr-4">
          <BarChart3 className="h-12 w-12 shrink-0 text-[#F4D58D]" />
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>Financial Insights</h2>
            <p className="text-sm sm:text-base opacity-90">Analytics • Ratios • Trends • Executive Summary</p>
          </div>
        </div>
        <button type="button" onClick={() => setOpen(!open)} className="flex-1 rounded-2xl border-2 border-[#f1d38c] bg-gradient-to-br from-[#fff4d1] to-[#f1d38c] p-3 text-left text-[#4a080e]" data-testid="or-exec-summary-toggle">
          <span className="flex items-start gap-3">
            <Sparkles className="h-7 w-7 shrink-0 text-[#a8781f]" />
            <span className="min-w-0 flex-1">
              <span className="block text-lg font-bold text-[#750817]">Executive Summary{periodLabel ? ` – ${periodLabel}` : ""}</span>
              <span className={`text-base ${open ? "block" : "line-clamp-2"}`} data-testid="or-exec-summary-text">{loading ? "Computing analytics…" : summary || "No summary available for this period."}</span>
            </span>
            {open ? <ChevronUp className="h-6 w-6 shrink-0" /> : <ChevronDown className="h-6 w-6 shrink-0" />}
          </span>
        </button>
      </div>
    </div>
  );
}

const CHIP = { green: "border-emerald-300 bg-emerald-50 text-emerald-900", amber: "border-amber-300 bg-amber-50 text-amber-900",
  red: "border-red-300 bg-red-50 text-red-900", blue: "border-sky-300 bg-sky-50 text-sky-900", purple: "border-violet-300 bg-violet-50 text-violet-900",
  neutral: "border-[#ead6a6] bg-white text-[#3b1a12]" };

export function MetalChip({ label, value, sub, tone = "neutral", testid }) {
  return (
    <div className={`min-w-0 rounded-xl border-2 px-3 py-2 text-center ${CHIP[tone] || CHIP.neutral}`} data-testid={testid}>
      <div className="text-xs font-semibold opacity-80">{label}</div>
      <div className="text-lg sm:text-xl font-bold leading-tight break-words" data-testid={testid && `${testid}-value`}>{value}</div>
      {sub && <div className="text-xs font-semibold">{sub}</div>}
    </div>
  );
}

export function SummaryStrip({ Icon, title, desc, basis, chips, open, onToggle, detailsLabel, testid, children }) {
  return (
    <section className="rounded-[20px] border-[3px] border-[#d9ae52] bg-gradient-to-br from-[#fffaf0] to-[#fbf0d6] p-3 sm:p-4 space-y-3" data-testid={testid}>
      <div className="flex flex-col xl:flex-row xl:items-center gap-3">
        <div className="flex items-center gap-3 xl:w-[300px] shrink-0">
          <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl text-[#F4D58D]" style={{ background: "linear-gradient(160deg,#a3242c,#6d0f17 60%,#4a080e)" }}><Icon className="h-8 w-8" /></span>
          <div className="min-w-0">
            <h3 className="text-xl font-bold leading-tight text-[#750817]" style={{ fontFamily: "'Playfair Display', serif" }}>{title}</h3>
            <p className="text-sm text-[#5a3a22] leading-tight">{desc}</p>
            {basis && <p className="text-xs font-semibold text-[#8a4f10] mt-0.5" data-testid={`${testid}-basis`}>{basis}</p>}
          </div>
        </div>
        <div className="grid flex-1 grid-cols-2 sm:grid-cols-[repeat(auto-fit,minmax(130px,1fr))] gap-2">{chips}</div>
        <button type="button" onClick={onToggle} className="shrink-0 inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-base font-bold text-[#fff3c4]"
          style={{ background: "linear-gradient(160deg,#8f1524,#750817 55%,#4D0712)" }} data-testid={`${testid}-toggle`}>
          {open ? <ChevronUp className="h-5 w-5" /> : <BarChart3 className="h-5 w-5" />}{open ? "Hide Details" : "View Details"}
        </button>
      </div>
      {!open && <button type="button" onClick={onToggle} className="flex w-full items-center gap-2 rounded-xl border border-[#ead6a6] bg-white/70 px-3 py-2 text-left text-base font-semibold text-[#750817]" data-testid={`${testid}-grid-toggle`}>
        <LayoutGrid className="h-5 w-5" /><span className="flex-1">{detailsLabel}</span><span className="hidden sm:inline text-sm text-[#5a3a22]">Click to expand</span><ChevronDown className="h-5 w-5" /></button>}
      {open && <div data-testid={`${testid}-details`}>{children}</div>}
    </section>
  );
}
